/**
 * PATH: packages/ui/src/components/settings/entities-tab/AddEntityForm.tsx
 * PURPOSE: Modal dialog for creating or editing an organisation entity
 * WHY: Extracted from EntitiesTab.tsx to keep files focused and under 300 lines
 *
 * FLOW:
 *   ┌─────────────┐    ┌───────────┐    ┌──────┐
 *   │ Open Dialog  │───▶│ Fill Form │───▶│ Save │
 *   └─────────────┘    └───────────┘    └──────┘
 *
 * DEPENDENCIES:
 *   - lucide-react: icons (X, Loader2, Check)
 *   - ./entities-tab-types: EntityFormState, OrganisationEntity
 */

import { useState, useMemo, useEffect } from 'react'
import { Loader2, X, Check } from 'lucide-react'
import type { EntityFormState, OrganisationEntity } from './entities-tab-types'

const COLOR_SWATCHES = [
  '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
  '#ec4899', '#f43f5e', '#ef4444', '#f97316',
  '#f59e0b', '#eab308', '#84cc16', '#22c55e',
  '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
  '#3b82f6', '#2563eb', '#4f46e5', '#7c3aed',
  '#64748b', '#475569', '#334155', '#1e293b',
]

function validateAbn(value: string): string | null {
  if (!value) return null
  const digits = value.replace(/[\s-]/g, '')
  if (!/^\d*$/.test(digits)) return 'Digits only'
  if (digits.length > 0 && digits.length !== 11) return `${digits.length}/11 digits`
  if (digits.length === 11 && digits[0] === '0') return 'Cannot start with 0'
  return null
}

function formatAbnDisplay(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 11)
  if (digits.length <= 2) return digits
  if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`
  if (digits.length <= 8) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`
  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`
}

export interface AddEntityFormProps {
  open: boolean
  editingEntity: OrganisationEntity | null
  formState: EntityFormState
  saving: boolean
  error?: string | null
  onFormChange: (updates: Partial<EntityFormState>) => void
  onClose: () => void
  onSave: () => void
}

export function AddEntityForm({
  open,
  editingEntity,
  formState,
  saving,
  error,
  onFormChange,
  onClose,
  onSave,
}: AddEntityFormProps) {
  const [abnDisplay, setAbnDisplay] = useState(() => formatAbnDisplay(formState.abn || ''))
  const [showCustomColor, setShowCustomColor] = useState(false)

  useEffect(() => {
    if (open) {
      setAbnDisplay(formatAbnDisplay(formState.abn || ''))
      setShowCustomColor(false)
    }
  }, [open, editingEntity?.id])

  const abnError = useMemo(() => validateAbn(formState.abn || ''), [formState.abn])
  const isAbnComplete = formState.abn?.replace(/[\s-]/g, '').length === 11
  const canSave = formState.name.trim().length > 0 && (!formState.abn || (isAbnComplete && !abnError))

  if (!open) return null

  const handleAbnChange = (raw: string) => {
    const digitsOnly = raw.replace(/\D/g, '').slice(0, 11)
    setAbnDisplay(formatAbnDisplay(digitsOnly))
    onFormChange({ abn: digitsOnly })
  }

  const inputClass = 'mt-1 w-full px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder:text-slate-400'

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 p-6 z-[60]">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-slate-900 dark:text-white">
            {editingEntity ? 'Edit Entity' : 'Create Entity'}
          </h4>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              value={formState.name ?? ''}
              onChange={(e) => onFormChange({ name: e.target.value })}
              placeholder="e.g. Bravada Group"
              className={inputClass}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Legal name</label>
            <input
              value={formState.legal_name ?? ''}
              onChange={(e) => onFormChange({ legal_name: e.target.value })}
              placeholder="Registered company name"
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Code</label>
              <input
                value={formState.code ?? ''}
                onChange={(e) => onFormChange({ code: e.target.value.toUpperCase() })}
                placeholder="BG"
                maxLength={10}
                className={inputClass}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">ABN</label>
              <input
                value={abnDisplay}
                onChange={(e) => handleAbnChange(e.target.value)}
                placeholder="XX XXX XXX XXX"
                maxLength={14}
                className={`${inputClass} ${abnError && formState.abn ? 'border-red-400 dark:border-red-600' : ''}`}
              />
              {abnError && formState.abn && (
                <p className="mt-1 text-xs text-red-500">{abnError}</p>
              )}
            </div>
          </div>

          {/* Color swatches */}
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Color</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {COLOR_SWATCHES.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => onFormChange({ color })}
                  className="w-7 h-7 rounded-lg border-2 transition-all flex items-center justify-center"
                  style={{
                    backgroundColor: color,
                    borderColor: formState.color === color ? '#fff' : 'transparent',
                    boxShadow: formState.color === color ? `0 0 0 2px ${color}` : 'none',
                  }}
                  title={color}
                >
                  {formState.color === color && <Check className="w-3.5 h-3.5 text-white drop-shadow-md" />}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setShowCustomColor((v) => !v)}
                className="w-7 h-7 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 text-slate-400 text-xs font-bold hover:border-slate-400 flex items-center justify-center"
                title="Custom color"
              >
                #
              </button>
            </div>
            {showCustomColor && (
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="color"
                  value={formState.color || '#6366f1'}
                  onChange={(e) => onFormChange({ color: e.target.value })}
                  className="w-8 h-8 p-0.5 border border-slate-200 dark:border-slate-700 rounded cursor-pointer"
                />
                <input
                  value={formState.color || '#6366f1'}
                  onChange={(e) => {
                    const hex = e.target.value
                    if (/^#[0-9a-fA-F]{0,6}$/.test(hex)) onFormChange({ color: hex })
                  }}
                  placeholder="#6366f1"
                  maxLength={7}
                  className="w-24 px-2 py-1 rounded text-xs font-mono border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                />
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-3 p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving || !canSave}
            className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
